<template>
  <v-dialog
    :model-value="modelValue"
    max-width="560"
    persistent
    @update:model-value="$emit('update:modelValue', $event)"
  >
    <v-card class="pa-2">
      <v-card-title class="text-h6">
        {{ $t("acceptance.pra.hold.dialog.title") }}
      </v-card-title>
      <v-card-subtitle class="pra-wrap">
        {{ $t("acceptance.pra.hold.dialog.subtitle") }}
      </v-card-subtitle>

      <v-card-text>
        <v-alert
          v-if="error"
          type="error"
          variant="tonal"
          density="compact"
          class="mb-4"
        >
          {{ error }}
        </v-alert>

        <v-select
          v-model="form.type"
          :items="typeItems"
          variant="outlined"
          density="comfortable"
          :label="`${$t('acceptance.pra.hold.dialog.type')} *`"
          :disabled="saving"
        />

        <v-textarea
          v-model="form.note"
          variant="outlined"
          density="comfortable"
          rows="3"
          auto-grow
          :counter="noteMax"
          :maxlength="noteMax"
          :label="`${$t('acceptance.pra.hold.dialog.note')} *`"
          :error-messages="noteMessage"
          :disabled="saving"
        />

        <div class="text-body-2 mt-2 mb-1">
          {{ $t("acceptance.pra.file") }}
        </div>
        <div class="pra-file-box d-flex flex-wrap align-center ga-2 pa-3">
          <v-chip
            v-if="form.file"
            size="small"
            variant="tonal"
            color="primary"
            closable
            :disabled="saving"
            @click:close="form.file = null"
          >
            {{ form.file.name }} · {{ fileSize }}
          </v-chip>
          <v-btn
            size="small"
            variant="outlined"
            color="primary"
            :disabled="saving"
            @click="pickFile"
          >
            {{
              form.file
                ? $t("acceptance.pra.crd.dialog.change_file")
                : $t("acceptance.pra.crd.dialog.choose_file")
            }}
          </v-btn>
          <input
            ref="fileInput"
            type="file"
            class="d-none"
            :accept="accept"
            @change="onFile"
          />
        </div>
        <div
          class="text-caption mt-1"
          :class="fileProblem ? 'text-error' : 'text-grey-darken-1'"
        >
          {{ fileProblem || $t("acceptance.pra.hold.dialog.file_hint") }}
        </div>
      </v-card-text>

      <v-card-actions>
        <v-spacer />
        <v-btn
          variant="outlined"
          color="primary"
          :disabled="saving"
          @click="$emit('update:modelValue', false)"
        >
          {{ $t("acceptance.pra.crd.dialog.cancel") }}
        </v-btn>
        <v-btn
          variant="flat"
          color="primary"
          :loading="saving"
          :disabled="!valid || saving"
          @click="submit"
        >
          {{ $t("acceptance.pra.hold.dialog.save") }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { FILE_TYPES } from "@/components/Acceptance/dossierFiles.mjs";
import { formatFileSize } from "@/components/Acceptance/praCrdForm.mjs";
import {
  HOLD_NOTE_MAX,
  HOLD_TYPES,
  emptyHoldForm,
  holdFormErrors,
} from "@/components/Acceptance/praHoldForm.mjs";

// "Metti in sospeso" (Stage 2 slice 8): the type, a required note (at most 1000 characters) and an
// optional file. While on hold no step can be recorded. The page sends it through the case session;
// a save error keeps the dialog open with the input and the server message.
export default {
  name: "AcceptancePraHoldDialog",
  props: {
    modelValue: { type: Boolean, default: false },
    saving: { type: Boolean, default: false },
    // Save failure from the page, shown inside the dialog; the input is kept.
    error: { type: String, default: "" },
  },
  emits: ["update:modelValue", "submit"],
  data: () => ({
    form: emptyHoldForm(),
  }),
  computed: {
    noteMax() {
      return HOLD_NOTE_MAX;
    },
    accept() {
      return FILE_TYPES.join(",");
    },
    typeItems() {
      return HOLD_TYPES.map((type) => ({
        value: type,
        title: this.$t(`acceptance.pra.hold.types.${type}`),
      }));
    },
    errors() {
      return holdFormErrors(this.form);
    },
    valid() {
      return Object.keys(this.errors).length === 0;
    },
    // An empty note only disables the save.
    noteMessage() {
      return this.errors.note === "too_long"
        ? [
            this.$t("acceptance.pra.crd.dialog.errors.too_long", {
              max: HOLD_NOTE_MAX,
            }),
          ]
        : [];
    },
    fileSize() {
      return this.form.file ? formatFileSize(this.form.file.size) : "";
    },
    fileProblem() {
      const reason = this.errors.file;
      return reason ? this.$t(`acceptance.files.errors.${reason}`) : "";
    },
  },
  watch: {
    modelValue(open) {
      if (open) this.form = emptyHoldForm();
    },
  },
  methods: {
    pickFile() {
      this.$refs.fileInput.click();
    },
    onFile(event) {
      const [file] = event.target.files || [];
      if (file) this.form.file = file;
      // The same file can be picked again after a change.
      event.target.value = ""; // eslint-disable-line no-param-reassign
    },
    submit() {
      if (!this.valid || this.saving) return;
      this.$emit("submit", {
        type: this.form.type,
        note: this.form.note.trim(),
        file: this.form.file,
      });
    },
  },
};
</script>

<style scoped>
.pra-wrap {
  white-space: normal;
}
.pra-file-box {
  border: 1px dashed rgba(0, 0, 0, 0.24);
  border-radius: 4px;
}
</style>
